import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import {
  Button,
  Empty,
  Table,
  Toast,
} from 'barte-design-system';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

import { Title } from '@/modules/shared/components';

import styles from './list.module.css';

import { TerminalStatus } from '@/modules/backoffice/types/terminal';
import type { ITerminalListItem } from '@/modules/backoffice/types/terminal';
import type { BatchActionMode, BatchOperationType } from '../components/drawer/batch-unlink-drawer/batch-unlink-drawer.type';
import { CsvImportModal, TerminalOperationDrawer } from '../components';
import { BatchUnlinkDrawer } from '../components/drawer/batch-unlink-drawer';
import type { TerminalFilterStatus } from '@/modules/backoffice/types/terminal';
import { TerminalListFilters } from '../components/terminal-list/terminal-list-filters/terminal-list-filters';
import { TerminalListTabs } from '../components/terminal-list/terminal-list-tabs/terminal-list-tabs';
import { TerminalListActiveFilters } from '../components/terminal-list/terminal-list-active-filters/terminal-list-active-filters';
import { TerminalListSelectionBar } from '../components/terminal-list/terminal-list-selection-bar/terminal-list-selection-bar';
import { TerminalDetailsDrawer } from '../components/terminal-details-drawer';
import { TestSuccessModal } from '../components/modal/test-success-modal';
import { TestFailureModal } from '../components/modal/test-failure-modal';
import { ConfirmActionModal } from '../components/modal/confirm-action-modal';
import { useTranslation } from 'react-i18next';
import { useTerminalListColumns, useTerminalListPage } from '@/modules/backoffice/hooks';

export const TerminalList = () => {
  const { t } = useTranslation();
  const page = useTerminalListPage();
  const filterWrapperRef = useRef<HTMLDivElement | null>(null);

  const STATUS_TABS: Array<{ key: TerminalFilterStatus; label: string }> = [
    { key: TerminalStatus.ALL, label: t('backoffice.terminalsTabsAll') },
    { key: TerminalStatus.TEST, label: t('backoffice.terminalsTabsTest') },
    { key: TerminalStatus.AVAILABLE, label: t('backoffice.terminalsTabsAvailable') },
    { key: TerminalStatus.LINKED, label: t('backoffice.terminalsTabsLinked') },
    { key: TerminalStatus.ACTIVE, label: t('backoffice.terminalsTabsActive') },
    { key: TerminalStatus.SUPPORT, label: t('backoffice.terminalsTabsSupport') },
    { key: TerminalStatus.INACTIVE, label: t('backoffice.terminalsTabsInactive') },
  ];

  const columns = useTerminalListColumns({
    isLoading: page.isLoading,
    styles,
    onCopySerialNumber: page.handleCopySerialNumber,
  });

  const showEmptyState =
    !page.isLoading &&
    (page.terminals.length === 0 || (page.pageable?.totalRecords || 0) === 0);

  const [isCsvImportModalOpen, setIsCsvImportModalOpen] = useState(false);
  const [isOperationDrawerOpen, setIsOperationDrawerOpen] = useState(false);
  const [isBatchUnlinkOpen, setIsBatchUnlinkOpen] = useState(false);
  const [batchActionMode, setBatchActionMode] = useState<BatchActionMode>('unlink');
  const [detailsBatchAction, setDetailsBatchAction] = useState<{
    terminal: ITerminalListItem;
    actionMode: BatchActionMode;
    batchOperationType?: BatchOperationType;
  } | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const mapBatchOperationToActionMode = (op: BatchOperationType): BatchActionMode => {
    const options: Partial<Record<BatchOperationType, BatchActionMode>> = {
      UNLINK_PAYFAC: 'unlink',
      DISASSOCIATE_SELLER: 'unlink',
      ASSOCIATE_SELLER: 'associate',
    };
    return options[op] ?? 'link';
  };

  const handleStartOperationFromDetails = (batchOperationType: BatchOperationType) => {
    if (!page.terminalDrawer) return;
    const serial = page.terminalDrawer.serialNumber;
    const terminal = page.terminals.find((t) => t.serialNumber === serial);
    if (!terminal) {
      page.pushToast({
        variant: 'error',
        title: t('backoffice.commonError'),
        description: t('backoffice.terminalsListTerminalNotFoundError'),
      });
      return;
    }
    setDetailsBatchAction({
      terminal,
      actionMode: mapBatchOperationToActionMode(batchOperationType),
      batchOperationType,
    });
    page.closeTerminalDrawer();
    setIsBatchUnlinkOpen(true);
  };
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenSearch = useCallback(() => {
    setIsSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);

  const handleSearchBlur = useCallback(() => {
    if (!page.filters.quickSearchSerial) {
      setIsSearchOpen(false);
    }
  }, [page.filters.quickSearchSerial]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!page.filters.isFilterOpen) return;

      const target = event.target as Node | null;

      if (
        filterWrapperRef.current &&
        target &&
        !filterWrapperRef.current.contains(target)
      ) {
        page.filters.closeFilter();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [page.filters.isFilterOpen, page.filters.closeFilter]);

  useEffect(() => {
    if (isCsvImportModalOpen || !!page.terminalDrawer) {
      page.filters.closeFilter();
    }
  }, [
    isCsvImportModalOpen,
    page.terminalDrawer,
    page.filters.closeFilter,
  ]);

  const batchDrawerProps = useMemo(() => {
    const closeBatchDrawer = () => {
      setIsBatchUnlinkOpen(false);
      setDetailsBatchAction(null);
    };
    if (detailsBatchAction) {
      return {
        onClose: closeBatchDrawer,
        selectedTerminals: [detailsBatchAction.terminal],
        currentStatus: detailsBatchAction.terminal.status,
        actionMode: detailsBatchAction.actionMode,
        initialOperationType: detailsBatchAction.batchOperationType,
        onSuccess: () => {},
      };
    }
    return {
      onClose: closeBatchDrawer,
      selectedTerminals: page.selectedRecords,
      currentStatus: page.status,
      actionMode: batchActionMode,
      initialOperationType: undefined,
      onSuccess: page.clearSelection,
    } as const;
  }, [detailsBatchAction, page.selectedRecords, page.status, batchActionMode, page.clearSelection]);

  const toastPortal =
    typeof document !== 'undefined'
      ? createPortal(
        <div className={styles['toast-container']}>
          {page.toasts.map((toast) => (
            <div key={toast.id} className={styles['toast-item']}>
              <Toast
                key={toast.id}
                variant={toast.variant}
                title={toast.title}
                description={toast.description}
                onClose={() => page.removeToast(toast.id)}
              />
            </div>
          ))}
        </div>,
        document.body,
      )
      : null;


  return (
    <>
      {toastPortal}

      <div className={styles.container}>
        <Helmet>
          <title>{t('backoffice.terminalsPageTitle')}</title>
        </Helmet>

        <div className={styles['page-header']}>
          <Title variant="h4" as="h1">
            {t('backoffice.terminalsPageHeading')}
          </Title>

          <Button
            onClick={() => {
              page.filters.closeFilter();
              setIsOperationDrawerOpen(true);
            }}
          >
            {t('backoffice.terminalsNewButton')}
          </Button>
        </div>

        <div className={styles.toolbar}>
          <TerminalListTabs
            tabs={STATUS_TABS}
            value={page.status}
            counts={page.tabCounts}
            onChange={page.handleChangeStatus}
          />

          <div className={styles['toolbar-actions']}>
            {isSearchOpen ? (
              <div className={styles['search-input-wrapper']}>
                <MagnifyingGlassIcon
                  size={15}
                  className={styles['search-input-icon']}
                />
                <input
                  ref={searchInputRef}
                  className={styles['search-input']}
                  placeholder={t('backoffice.terminalsSearchPlaceholder')}
                  value={page.filters.quickSearchSerial}
                  onChange={(e) => page.filters.setQuickSearchSerial(e.target.value)}
                  onBlur={handleSearchBlur}
                />
              </div>
            ) : (
              <button
                type="button"
                className={styles['toolbar-btn']}
                onClick={handleOpenSearch}
              >
                <MagnifyingGlassIcon size={16} />
                {t('backoffice.terminalsSearchButton')}
              </button>
            )}

            <div ref={filterWrapperRef}>
              <TerminalListFilters
                isOpen={page.filters.isFilterOpen}
                isActive={page.filters.hasActiveFilters}
                openSections={page.filters.openFilterSections}
                draftFilters={page.filters.draftFilters}
                onToggleOpen={page.filters.toggleFilterOpen}
                onClose={page.filters.closeFilter}
                onToggleSection={page.filters.toggleFilterSection}
                onDraftFiltersChange={page.filters.updateDraftFilters}
                onAddSerial={page.filters.handleAddSerialFilter}
                onRemoveSerial={page.filters.handleRemoveSerialFilter}
                onApply={page.filters.handleApplyFilters}
                onClearAll={page.filters.handleClearAllFilters}
              />
            </div>
          </div>
        </div>

        <TerminalListSelectionBar
          selectedCount={page.selectedKeys.length}
          currentStatus={page.status}
          onClearSelection={page.clearSelection}
          onAction={() => {
            const modeByStatus: Partial<Record<TerminalStatus, BatchActionMode>> = {
              [TerminalStatus.AVAILABLE]: 'link',
              [TerminalStatus.LINKED]: 'associate',
              [TerminalStatus.ACTIVE]: 'unlink',
            };
            setBatchActionMode(modeByStatus[page.status as TerminalStatus] ?? 'link');
            setIsBatchUnlinkOpen(true);
          }}
          onSecondaryAction={() => { setBatchActionMode('unlink'); setIsBatchUnlinkOpen(true); }}
          onTestSuccess={page.openBatchSuccessModal}
          onTestFailure={page.openBatchFailureModal}
        />

        <TerminalListActiveFilters
          filters={page.filters.appliedFilters}
          onRemove={page.filters.handleRemoveAppliedFilter}
        />

        {showEmptyState && <Empty showButton={false} />}

        {!showEmptyState && (
          <Table
            className={styles['table-root']}
            columns={columns}
            data={page.isLoading ? page.fakeData : page.terminals}
            rowKey={(record) => record.id}
            rowSelection={{
              selectedRowKeys: page.selectedKeys,
              onChange: (keys) => page.setSelectedKeys(keys),
            }}
            onRowClick={(record) => {
              page.openTerminalDrawer(record.serialNumber);
            }}
            pagination={{
              currentPage: page.page,
              totalPages: page.pageable?.totalPages || 1,
              onPageChange: page.setPage,
              pageSize: page.pageSize,
              pageSizeOptions: [10, 25, 50, 100],
              onPageSizeChange: page.setPageSize,
              totalItems: page.pageable?.totalRecords || 0,
            }}
            size="sm"
          />
        )}

        <TerminalOperationDrawer
          isOpen={isOperationDrawerOpen}
          onClose={() => setIsOperationDrawerOpen(false)}
          onToast={page.pushToast}
          initialSerialNumbers={page.selectedRecords.map((r) => r.serialNumber)}
        />

        <BatchUnlinkDrawer isOpen={isBatchUnlinkOpen} {...batchDrawerProps} />

        <CsvImportModal
          isOpen={isCsvImportModalOpen}
          onClose={() => setIsCsvImportModalOpen(false)}
        />

        <TerminalDetailsDrawer
          isOpen={!!page.terminalDrawer}
          serialNumber={page.terminalDrawer?.serialNumber ?? ''}
          onClose={page.closeTerminalDrawer}
          onOpenSuccessModal={page.openSuccessModal}
          onOpenFailureModal={page.openFailureModal}
          onStartOperation={handleStartOperationFromDetails}
        />

        <TestSuccessModal
          isOpen={page.isSuccessModalOpen}
          isLoading={page.isApproving}
          onConfirm={async () => { await page.handleApproveTest(); }}
          onClose={page.closeSuccessModal}
        />

        <TestFailureModal
          isOpen={page.isFailureModalOpen}
          isLoading={page.isRejecting}
          onConfirm={async (reason) => { await page.handleRejectTest(reason); }}
          onClose={page.closeFailureModal}
        />

        <ConfirmActionModal
          isOpen={page.isBatchSuccessModalOpen}
          title={t('backoffice.terminalsBatchTestSuccessModalTitle')}
          description={t('backoffice.terminalsBatchTestSuccessModalDescription', { count: page.selectedKeys.length })}
          confirmLabel={t('backoffice.terminalsBatchTestSuccessModalConfirm')}
          cancelLabel={t('backoffice.terminalsSelectionCancel')}
          isLoading={page.isBatchProcessing}
          onConfirm={page.handleBatchApproveTest}
          onClose={page.closeBatchSuccessModal}
        />

        <TestFailureModal
          isOpen={page.isBatchFailureModalOpen}
          isLoading={page.isBatchProcessing}
          onConfirm={async (reason) => { await page.handleBatchRejectTest(reason); }}
          onClose={page.closeBatchFailureModal}
        />
      </div>
    </>
  );
};
